export type OpportunityType =
  | "AI_OPERATOR_BUILD"
  | "BUSINESS_SYSTEMS_AUDIT"
  | "APP_BUILD"
  | "APP_RESCUE"
  | "VENTURE_DEAL_EVALUATION"
  | "GENERAL_INQUIRY";

export interface OpportunityDetailsByType {
  AI_OPERATOR_BUILD: {
    operatorScope: string;
    systems: string;
    approvalPoints: string;
  };
  BUSINESS_SYSTEMS_AUDIT: {
    bottlenecks: string;
    manualTasks: string;
    revenueLeaks: string;
  };
  APP_BUILD: {
    usersAndJob: string;
    currentProcess: string;
    mustWorkFirst: string;
  };
  APP_RESCUE: {
    appUrl: string;
    currentStack: string;
    brokenOrUnfinished: string;
  };
  VENTURE_DEAL_EVALUATION: {
    assetType: string;
    assetName: string;
    evaluationGoal: string;
  };
  GENERAL_INQUIRY: {
    topic: string;
  };
}

export type OpportunityDetails<T extends OpportunityType = OpportunityType> =
  OpportunityDetailsByType[T] & Record<string, string>;

export interface OpportunityPayload<T extends OpportunityType = OpportunityType> {
  source: "shockvaluemedia.com";
  entryPoint: string;
  opportunityType: T;
  contact: {
    name: string;
    email: string;
    phone: string;
    company: string;
    role: string;
  };
  business: {
    website: string;
    industry: string;
    companyStage: string;
    companySize: string;
    revenueRange: string;
    currentTools: string[];
  };
  request: {
    summary: string;
    painPoints: string[];
    desiredOutcome: string;
    timeline: string;
    budgetRange: string;
    urgency: string;
  };
  qualification: {
    score: number | null;
    signals: string[];
    estimatedValue: number | null;
  };
  context: {
    utm: Record<string, string>;
    referrer: string;
    pageUrl: string;
    submittedAt: string;
    submissionId: string;
    internalVerification: boolean;
    verificationLabel: string;
  };
  details: OpportunityDetails<T>;
}

export interface EntryPointConfig {
  entryPoint: string;
  opportunityType: OpportunityType;
  path: string;
  title: string;
  eyebrow: string;
  description: string;
  primaryCta: string;
  fields: Array<{
    name: string;
    label: string;
    placeholder: string;
    help?: string;
    type?: "text" | "textarea" | "select";
    options?: string[];
  }>;
}

export const entryPointConfigs: Record<string, EntryPointConfig> = {
  aiOperator: {
    entryPoint: "build_ai_operator",
    opportunityType: "AI_OPERATOR_BUILD",
    path: "/ai-operators",
    title: "Build Your SVM Operator",
    eyebrow: "Operator build",
    description:
      "For teams that need one clear place to see incoming work, decisions, owners, and follow-up across the tools they already use.",
    primaryCta: "Send this to SVM",
    fields: [
      {
        name: "operatorScope",
        label: "What should the operator help run?",
        placeholder: "For example: new client requests, approvals, delivery updates, or weekly follow-up.",
        help: "Describe the part of the business that is hardest to keep organized. You do not need to design the system.",
        type: "textarea",
      },
      {
        name: "systems",
        label: "Where does this work live now?",
        placeholder: "For example: Gmail, HubSpot, Slack, spreadsheets, a calendar, or paper notes.",
        help: "Name whatever your team actually uses, even if it feels messy or basic.",
      },
      {
        name: "approvalPoints",
        label: "Which decisions still need a person?",
        placeholder: "For example: approving a proposal, sending a client message, or changing a deadline.",
        help: "SVM keeps important judgment with your team. Tell us where a person must stay in control.",
      },
    ],
  },
  systemsAudit: {
    entryPoint: "audit_business_systems",
    opportunityType: "BUSINESS_SYSTEMS_AUDIT",
    path: "/systems-audit",
    title: "Audit My Business Systems",
    eyebrow: "Systems audit",
    description:
      "For teams that know work is getting lost, repeated, or delayed but are not sure which process or tool to fix first.",
    primaryCta: "Send this to SVM",
    fields: [
      {
        name: "bottlenecks",
        label: "Where does work slow down or get lost?",
        placeholder: "For example: missed leads, late approvals, repeated data entry, or unclear ownership.",
        help: "Start with the moment that causes the most frustration, delay, or rework.",
        type: "textarea",
      },
      {
        name: "manualTasks",
        label: "What does your team repeat by hand?",
        placeholder: "For example: copying details between tools, sending reminders, or building the same report.",
        help: "Ordinary work counts. We are looking for repetition, not technical language.",
      },
      {
        name: "revenueLeaks",
        label: "What is the business cost?",
        placeholder: "For example: slower sales, missed follow-up, unhappy clients, or too much staff time.",
        help: "A rough answer is enough. This helps us focus the review on what matters most.",
      },
    ],
  },
  appBuild: {
    entryPoint: "build_app",
    opportunityType: "APP_BUILD",
    path: "/app-build",
    title: "Build an App That Fits the Work",
    eyebrow: "App build",
    description:
      "For organizations that need a new customer, staff, or operations app and want help turning the business need into a focused first release.",
    primaryCta: "Send this app idea to SVM",
    fields: [
      {
        name: "usersAndJob",
        label: "Who will use the app, and what do they need to do?",
        placeholder: "For example: clients submit requests, staff review them, and a manager approves the next step.",
        help: "Focus on the people and the job. SVM will translate that into product and technical requirements.",
        type: "textarea",
      },
      {
        name: "currentProcess",
        label: "How is this handled today?",
        placeholder: "For example: email, spreadsheets, forms, meetings, or a tool that no longer fits.",
        help: "If there is no current process, tell us what people do instead.",
      },
      {
        name: "mustWorkFirst",
        label: "What must the first useful version do well?",
        placeholder: "Name the one result that would make the first release worth using.",
        help: "You do not need a feature list. We will help separate the first release from later ideas.",
        type: "textarea",
      },
    ],
  },
  appRescue: {
    entryPoint: "rescue_finish_app",
    opportunityType: "APP_RESCUE",
    path: "/app-rescue",
    title: "App Rescue Sprint",
    eyebrow: "App rescue",
    description:
      "For stuck apps, previews, repos, MVPs, and no-code builds that need a trustworthy launch decision before more features are added.",
    primaryCta: "Request an App Rescue Review",
    fields: [
      {
        name: "appUrl",
        label: "Share the app, repo, preview, or walkthrough",
        placeholder: "A live URL, preview, GitHub repo, no-code project, Loom walkthrough, or product link.",
        help: "Share only what you are comfortable putting in a form. Access and credentials can be handled privately later.",
      },
      {
        name: "currentStack",
        label: "What was it built with, if you know?",
        placeholder: "For example: Lovable, React, Supabase, Firebase, AWS, Stripe, Vercel, or 'not sure'.",
        help: "It is completely fine to write 'not sure.' We can identify the technology during review.",
      },
      {
        name: "brokenOrUnfinished",
        label: "What is blocking launch or real use?",
        placeholder: "Tell us what fails, feels unfinished, confuses users, or keeps the app from being trusted.",
        help: "Describe what you see. You do not need to diagnose the technical cause.",
        type: "textarea",
      },
    ],
  },
  ventureEvaluation: {
    entryPoint: "evaluate_domain_venture_deal",
    opportunityType: "VENTURE_DEAL_EVALUATION",
    path: "/venture-evaluation",
    title: "Evaluate a Domain, Venture, or Deal",
    eyebrow: "Venture evaluation",
    description:
      "For a domain, app, audience, partnership, acquisition, or client opportunity that needs a clear next decision before more time or money goes in.",
    primaryCta: "Send this decision to SVM",
    fields: [
      {
        name: "assetType",
        label: "Asset type",
        placeholder: "Choose one",
        help: "Pick the closest match. SVM can adjust it during review.",
        type: "select",
        options: ["Domain", "App", "Business", "Audience", "Partnership", "Acquisition", "Client deal"],
      },
      {
        name: "assetName",
        label: "Asset or opportunity name",
        placeholder: "Domain, app, company, partner, or deal name.",
        help: "A working name is fine if the opportunity is still early.",
      },
      {
        name: "evaluationGoal",
        label: "Decision needed",
        placeholder: "For example: build, buy, sell, partner, pause, or pass.",
        help: "Tell us what choice you are trying to make, even if the options are not settled.",
      },
    ],
  },
  general: {
    entryPoint: "general_contact",
    opportunityType: "GENERAL_INQUIRY",
    path: "/contact",
    title: "Start a Conversation",
    eyebrow: "General intake",
    description:
      "Use this when you know what is frustrating or important but do not yet know whether the answer is an app, automation, or a better process.",
    primaryCta: "Send this to SVM",
    fields: [
      {
        name: "topic",
        label: "What is happening, and what would you like to change?",
        placeholder: "Describe the problem in your own words. SVM will help translate it into a practical next step.",
        help: "You do not need to know what technology you need.",
        type: "textarea",
      },
    ],
  },
};

export function buildOpportunityPayload(args: {
  config: EntryPointConfig;
  form: Record<string, string>;
  details: Record<string, string>;
  submissionId?: string;
}): OpportunityPayload {
  const context = readOpportunityContext();
  const tools = args.form.currentTools
    ? args.form.currentTools.split(",").map((tool) => tool.trim()).filter(Boolean)
    : [];

  const painPoints = args.form.painPoints
    ? args.form.painPoints.split(",").map((point) => point.trim()).filter(Boolean)
    : [];

  const signals = [
    args.form.timeline,
    args.form.urgency,
    args.form.budgetRange,
    args.config.opportunityType,
  ].filter(Boolean);

  const estimatedValue = estimateOpportunityValue(args.form.budgetRange || "");
  const request = {
    summary: args.form.summary || "",
    painPoints,
    desiredOutcome: args.form.desiredOutcome || "",
    timeline: args.form.timeline || "",
    budgetRange: args.form.budgetRange || "",
    urgency: args.form.urgency || "",
  };
  const contact = {
    name: args.form.name || "",
    email: args.form.email || "",
    phone: args.form.phone || "",
    company: args.form.company || "",
    role: args.form.role || "",
  };

  return {
    source: "shockvaluemedia.com",
    entryPoint: args.config.entryPoint,
    opportunityType: args.config.opportunityType,
    contact,
    business: {
      website: args.form.website || "",
      industry: args.form.industry || "",
      companyStage: args.form.companyStage || "",
      companySize: args.form.companySize || "",
      revenueRange: args.form.revenueRange || "",
      currentTools: tools,
    },
    request,
    qualification: {
      score: scoreOpportunity({ contact, request, estimatedValue }),
      signals,
      estimatedValue,
    },
    context: {
      ...context,
      submittedAt: new Date().toISOString(),
      submissionId: args.submissionId || generateSubmissionId(),
    },
    details: buildOpportunityDetails(args.config, args.form, args.details),
  };
}

/**
 * Best-effort dollar estimate parsed from a free-text budget range such as
 * "$15k-$50k", "$50k+", or "$20,000". Returns the midpoint of a range, a
 * modest uplift for open-ended "+" values, or null when nothing parses.
 * This is a triage hint for Operator, not a quote.
 */
export function estimateOpportunityValue(budgetRange: string): number | null {
  const text = budgetRange.toLowerCase();
  if (!text.trim() || text.includes("unsure") || text.includes("not sure")) {
    return null;
  }

  const matches = [...text.matchAll(/(\d[\d,.]*)\s*([km])?/g)]
    .map((match) => {
      const base = Number(match[1].replace(/,/g, ""));
      if (!Number.isFinite(base)) {
        return null;
      }
      const suffix = match[2];
      if (suffix === "k") return base * 1_000;
      if (suffix === "m") return base * 1_000_000;
      return base;
    })
    .filter((value): value is number => value !== null && value > 0);

  if (matches.length === 0) {
    return null;
  }

  if (matches.length === 1) {
    // "$50k+" reads as a floor; nudge up so an open top end isn't undervalued.
    return text.includes("+") ? Math.round(matches[0] * 1.5) : matches[0];
  }

  const low = Math.min(...matches);
  const high = Math.max(...matches);
  return Math.round((low + high) / 2);
}

/**
 * Coarse 0-100 qualification score from budget, urgency, timeline, identity,
 * and brief detail. A directional triage signal for Operator, not a verdict.
 */
export function scoreOpportunity(args: {
  contact: { company: string; email: string };
  request: { budgetRange: string; timeline: string; urgency: string; summary: string };
  estimatedValue: number | null;
}): number {
  let score = 25;

  if (args.estimatedValue != null) {
    score += args.estimatedValue >= 50_000 ? 30 : args.estimatedValue >= 15_000 ? 22 : 12;
  }

  const urgency = `${args.request.urgency} ${args.request.timeline}`.toLowerCase();
  if (/(critical|high|now|this month|asap|urgent)/.test(urgency)) {
    score += 20;
  } else if (/(this quarter|soon|month)/.test(urgency)) {
    score += 10;
  }

  if (args.contact.company.trim()) {
    score += 10;
  }

  if (args.request.summary.trim().length >= 120) {
    score += 15;
  } else if (args.request.summary.trim().length >= 40) {
    score += 8;
  }

  return Math.max(0, Math.min(100, score));
}

function generateSubmissionId(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `sub-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

export function readOpportunityContext(pageUrl?: string) {
  const params = readSearchParams(pageUrl);

  return {
    utm: collectUtmParams(params),
    referrer: typeof document === "undefined" ? "" : document.referrer,
    pageUrl: pageUrl || (typeof window === "undefined" ? "" : window.location.href),
    internalVerification: isInternalVerification(params),
    verificationLabel:
      params.get("svm_verification_label")?.trim() || "public-intake-internal-check",
  };
}

function readSearchParams(pageUrl?: string) {
  if (pageUrl) {
    return new URL(pageUrl).searchParams;
  }

  if (typeof window === "undefined") {
    return new URLSearchParams();
  }

  try {
    return new URL(window.location.href).searchParams;
  } catch {
    return new URLSearchParams(window.location.search);
  }
}

function collectUtmParams(params: URLSearchParams): Record<string, string> {
  return ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].reduce(
    (utm, key) => {
      const value = params.get(key)?.trim();
      if (value) {
        utm[key] = value;
      }
      return utm;
    },
    {} as Record<string, string>
  );
}

function isInternalVerification(params: URLSearchParams): boolean {
  const value = params.get("svm_internal_check")?.toLowerCase().trim();
  return value === "1" || value === "true";
}

function buildOpportunityDetails(
  config: EntryPointConfig,
  form: Record<string, string>,
  extraDetails: Record<string, string>
): OpportunityDetails {
  const entryDetails = Object.fromEntries(
    config.fields.map((field) => [field.name, form[field.name] || ""])
  );

  return {
    ...extraDetails,
    ...entryDetails,
  } as OpportunityDetails;
}
